import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  ActivationReview, SeedPlan, UsedBy, WorkflowView, applySeeds, approveWorkflowVersion, deleteAutomationWorkflow,
  getActivationReview, getSeedPlan, pauseAllWorkflows, resumeAllWorkflows, setWorkflowState,
} from '../../../api/cafAutomation';
import { actionCopy } from '../copy';
import { formatCount } from '../format';
import { LenderRule, lenderFor, senderEmail, useLenderRulesReady } from '../lenders';
import { Modal } from '../Overlay';
import { MSG_NOT_CONFIRMED, isClash, runSave } from '../saveEngine';
import { useToast } from '../ToastContext';
import { readinessSentence, seedBlocked } from './automationCopy';
import { NoteField, SaveProblem } from './formBits';
import TemplateSourceEditor from '../../caf-workflows/TemplateSourceEditor';

export interface TeachTarget { entryId: string; sender: string }

interface ConfirmState {
  note: string;
  setNote: (value: string) => void;
  saving: boolean;
  problem: string;
  clear: () => void;
  save: () => Promise<void>;
}

/** One save with runSave: a refusal shows in the confirm (clashes offer Reload), anything else also toasts. */
const useConfirmSave = <T,>(attempt: (note: string) => Promise<T>, done: (value: T | null) => void,
  options?: { onFailure?: () => void; canSave?: () => boolean; noRetry?: boolean }): ConfirmState => {
  const toast = useToast();
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');
  const save = async () => {
    if (saving || (options?.canSave && !options.canSave())) return;
    setSaving(true);
    setProblem('');
    const result = await runSave(() => attempt(note.trim()));
    setSaving(false);
    if (!result.ok) {
      setProblem(result.message);
      options?.onFailure?.();
      // An unknown outcome is never retried from a button: the write may have landed.
      if (!isClash(result.message)) toast.failed(result.message, options?.noRetry || result.message === MSG_NOT_CONFIRMED ? undefined : () => { void save(); });
      return;
    }
    done(result.value);
  };
  return { note, setNote, saving, problem, clear: () => setProblem(''), save };
};

const ConfirmShell: React.FC<{
  title: string; button: string; danger?: boolean; state: ConfirmState; disabled?: boolean;
  onClose: () => void; onReload: () => void; children: React.ReactNode;
}> = ({ title, button, danger = false, state, disabled = false, onClose, onReload, children }) => {
  const id = useId();
  return (
    <Modal title={title} onClose={onClose} busy={state.saving}
      footer={(
        <>
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose} disabled={state.saving}>Cancel</button>
          <button type="button" className={`ui-btn ui-btn--${danger ? 'danger-solid' : 'primary'} ui-btn--md`}
            disabled={state.saving || disabled} onClick={() => { void state.save(); }}>
            {state.saving ? <><span className="cur-spin" aria-hidden="true" />Saving…</> : button}
          </button>
        </>
      )}>
      {children}
      <SaveProblem message={state.problem} onReload={() => { state.clear(); onReload(); }} />
      <NoteField id={`${id}-note`} value={state.note} onChange={state.setNote} disabled={state.saving} />
    </Modal>
  );
};

const withNote = (note: string) => (note ? { note } : {});

export const StateConfirm: React.FC<{
  view: WorkflowView; to: 'watching' | 'paused'; onClose: () => void; onDone: () => void; onReload: () => void;
}> = ({ view, to, onClose, onDone, onReload }) => {
  const toast = useToast();
  const state = useConfirmSave(
    (note) => setWorkflowState(view.workflow_id, { to, expected_revision: view.revision, ...withNote(note) }),
    () => {
      toast.saved(to === 'paused' ? 'Saved. It is paused.' : 'Saved. It is watching again.');
      onDone();
      onClose();
    },
  );
  const running = view.state === 'on' ? ['Runs already going stop before their next step.'] : [];
  const lines = to === 'watching'
    ? [`${view.name} stops acting and goes back to recording what it would have done. Emails without an active workflow need manual handling.`, ...running]
    : [`${view.name} stops completely: it doesn't act and doesn't record. Emails without an active workflow need manual handling.`, ...running];
  return (
    <ConfirmShell title={to === 'watching' ? 'Back to watching?' : 'Pause this workflow?'}
      button={to === 'watching' ? 'Back to watching' : 'Pause'} danger={to === 'paused'} state={state}
      onClose={onClose} onReload={onReload}>
      {lines.map((line) => <p key={line}>{line}</p>)}
    </ConfirmShell>
  );
};

/** A publication review is fetched when opened; the server checks it again under the activation lock. */
export const ActivationConfirm: React.FC<{
  view: WorkflowView; overallMode: 'off' | 'watching' | 'on'; approve?: boolean;
  onClose: () => void; onDone: () => void; onReload: () => void;
}> = ({ view, overallMode, approve = false, onClose, onDone, onReload }) => {
  const toast = useToast();
  const [review, setReview] = useState<ActivationReview | null>(null);
  const [loadProblem, setLoadProblem] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [recovery, setRecovery] = useState<{ priorView: WorkflowView; targetReviewKey: number | null; reviewReady: boolean } | null>(null);
  useEffect(() => {
    let active = true;
    setReview(null);
    getActivationReview(view.workflow_id).then(
      (value) => { if (active) {
        setReview(value); setLoadProblem('');
        setRecovery((current) => current?.targetReviewKey === reloadKey ? { ...current, reviewReady: true } : current);
      } },
      () => { if (active) setLoadProblem("Couldn't check this version. Reload the review."); },
    );
    return () => { active = false; };
  }, [view.workflow_id, reloadKey]);
  useEffect(() => {
    if (recovery?.reviewReady && view !== recovery.priorView) setRecovery(null);
  }, [recovery, view]);
  const stale = Boolean(review && (review.revision !== view.revision || review.version !== view.current_version));
  const blocked = !review || stale || !!recovery || review.version == null || review.needs_check || review.blockers.length > 0;
  const state = useConfirmSave(
    (note) => {
      if (!review || review.version == null) throw new Error('Review a published version first.');
      const body = { version: review.version, expected_revision: review.revision, ...withNote(note) };
      return approve ? approveWorkflowVersion(view.workflow_id, body)
        : setWorkflowState(view.workflow_id, { to: 'on', ...body });
    },
    () => { toast.saved(approve ? 'Approved the reviewed version.' : 'Workflow turned on.'); onDone(); onClose(); },
    { onFailure: () => setRecovery({ priorView: view, targetReviewKey: null, reviewReady: false }),
      canSave: () => !blocked, noRetry: true },
  );
  const reload = () => {
    state.clear();
    setRecovery({ priorView: view, targetReviewKey: reloadKey + 1, reviewReady: false });
    onReload();
    setReloadKey((value) => value + 1);
  };
  return (
    <ConfirmShell title={approve ? 'Approve this version?' : 'Turn on this workflow?'}
      button={approve ? 'Approve this version' : 'Turn on this workflow'} state={state} disabled={blocked}
      onClose={onClose} onReload={reload}>
      {loadProblem && <p role="alert">{loadProblem}</p>}
      {!review && !loadProblem && <p>Checking the published version…</p>}
      {review && <>
        <p>Published version {review.version}. {review.previous_active_version == null
          ? 'No version is active yet.' : `Version ${review.previous_active_version} is active now.`}</p>
        <p>{review.watched_versions.length ? `Observed versions: ${review.watched_versions.join(', ')}.` : 'No published version has been observed in Watching yet.'}</p>
        {stale && <p role="alert">This workflow changed since the review opened. Reload the review before continuing.</p>}
        {review.watched_versions.includes(review.version ?? -1)
          ? <p>This exact version has been observed in Watching.</p>
          : <p role="status">This is not the version observed in Watching. Check its steps and email configuration before proceeding.</p>}
        <p>{overallMode === 'watching' ? 'The overall AI is Watching. Turning this workflow on records decisions only until the overall AI is On.'
          : overallMode === 'off' ? 'The overall AI is Off. This workflow will wait until the overall AI is On.'
            : 'Eligible new and waiting emails can start this workflow.'}</p>
        {review.changes.length > 0 && <section aria-label="Changes since the active version">
          <h3>Changes since the active version</h3>
          <ul>{review.changes.map((change, index) => <li key={index}>{change.field === 'configuration'
            ? 'Email source or configuration changed; check its client emails.' : 'Workflow steps changed; check the new version.'}</li>)}</ul>
        </section>}
        {review.needs_check && <p role="alert">Check and preview its client emails first.</p>}
        {review.blockers.length > 0 && <div role="alert">
          <p>{approve ? 'This version cannot be approved yet:' : 'This workflow cannot be turned on yet:'}</p>
          <ul>{Array.from(new Set(review.blockers.map((blocker) => blocker === 'client_emails_not_checked'
            ? 'Check and preview its client emails.' : readinessSentence(blocker)))).map((line) => <li key={line}>{line}</li>)}</ul>
        </div>}
      </>}
      {(loadProblem || ((stale || recovery) && !state.problem)) &&
        <button type="button" className="cur-linkish" onClick={reload}>Reload review and workflow</button>}
    </ConfirmShell>
  );
};

export const PauseAllConfirm: React.FC<{
  pause: boolean; revision: number; onClose: () => void; onDone: () => void; onReload: () => void;
}> = ({ pause, revision, onClose, onDone, onReload }) => {
  const toast = useToast();
  const state = useConfirmSave(
    (note) => (pause ? pauseAllWorkflows : resumeAllWorkflows)({ expected_revision: revision, ...withNote(note) }),
    () => {
      toast.saved(pause ? 'Saved. All workflows are paused.' : 'Saved. Workflows are back to their own settings.');
      onDone();
      onClose();
    },
  );
  return (
    <ConfirmShell title={pause ? 'Pause all workflows?' : 'Resume workflows?'}
      button={pause ? 'Pause all workflows' : 'Resume workflows'} danger={pause} state={state} onClose={onClose} onReload={onReload}>
      {pause ? (
        <>
          <div className="cur-callout cur-callout--warn">Every workflow stops before its next step. New unmatched decisions need manual handling.</div>
          <p className="cur-hint">
            For emergencies. The AI switch doesn&apos;t change, and each workflow keeps its own setting, so Resume workflows puts everything back as it was.
          </p>
        </>
      ) : <p>Every workflow goes back to its own setting.</p>}
    </ConfirmShell>
  );
};

export const DeleteDraftConfirm: React.FC<{
  view: WorkflowView; onClose: () => void; onDone: () => void; onReload: () => void;
}> = ({ view, onClose, onDone, onReload }) => {
  const toast = useToast();
  const state = useConfirmSave(
    (note) => deleteAutomationWorkflow(view.workflow_id, { expected_revision: view.revision, ...withNote(note) }),
    () => { toast.saved('Deleted.'); onDone(); onClose(); },
  );
  return (
    <ConfirmShell title="Delete this draft?" button="Delete draft" danger state={state} onClose={onClose} onReload={onReload}>
      <p>{`${view.name} has never run or watched, so nothing else changes.`}</p>
      <p>{view.used_by.length ? 'Its examples go back to having no workflow.' : 'No examples use it.'}</p>
      <p>This can&apos;t be undone.</p>
    </ConfirmShell>
  );
};

export const SeedsConfirm: React.FC<{ onClose: () => void; onDone: () => void }> = ({ onClose, onDone }) => {
  const toast = useToast();
  const [plan, setPlan] = useState<SeedPlan | null>(null);
  const [loadProblem, setLoadProblem] = useState('');
  const [loading, setLoading] = useState(true), [editorBusy, setEditorBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null), [notice, setNotice] = useState('');
  const request = useRef(0), status = useRef<HTMLParagraphElement>(null);
  const load = useCallback((afterImport = false) => {
    const current = ++request.current;
    setLoading(true);
    setLoadProblem('');
    setNotice('');
    getSeedPlan().then(value => {
      if (current !== request.current) return;
      setPlan(value);
      setLoading(false);
      if (afterImport) {
        setNotice('Source imported. Readiness and counts refreshed.');
        setEditorBusy(false);
      }
    }, () => {
      if (current !== request.current) return;
      setLoading(false);
      setLoadProblem("Couldn't check what is set up. Reload readiness to try again.");
      if (afterImport) setEditorBusy(false);
    });
  }, []);
  useEffect(() => { load(); return () => { request.current++; }; }, [load]);
  useEffect(() => { if (notice) status.current?.focus(); }, [notice]);
  const state = useConfirmSave(
    (note) => applySeeds({ expected_counts: plan ? plan.counts : { seeds: 0, examples: 0, rules: 0 }, ...withNote(note) }),
    (result) => {
      const created = result ? result.counts.seeds : 0;
      toast.saved(created === 0
        ? "They're already set up."
        : `Saved. ${created} built-in ${created === 1 ? 'workflow is' : 'workflows are'} watching.`);
      onDone();
      onClose();
    },
  );
  const counts = plan?.counts;
  const blocked = plan ? plan.items.filter((item) => item.status === 'blocked') : [];
  const nothing = Boolean(counts && !counts.seeds && !counts.examples && !counts.rules);
  return (
    <ConfirmShell title="Set up the built-in workflows?" button="Set them up" state={state} disabled={!plan || nothing || loading || !!loadProblem || editorBusy}
      onClose={onClose} onReload={() => load()}>
      {loadProblem && <p className="cur-hint">{loadProblem}</p>}
      <button type="button" className="cur-linkish" disabled={editorBusy || state.saving} onClick={() => load()}>Reload readiness</button>
      {loading && <p className="cur-hint">Checking…</p>}
      {notice && <p role="status" tabIndex={-1} ref={status}>{notice}</p>}
      {counts && (nothing ? (blocked.length ? null : <p>Everything is already set up.</p>) : (
        <p>
          {`This creates ${formatCount(counts.seeds)} ${counts.seeds === 1 ? 'workflow' : 'workflows'} from the configured built-in actions, `
            + `and attaches them to ${formatCount(counts.examples)} ${counts.examples === 1 ? 'example' : 'examples'}. `
            + 'They start in Watching, recording proposed actions without sending or changing tasks.'}
        </p>
      ))}
      {counts && counts.rules > 0 && <p>The one for cancelled claims is used for lender emails on cancelled claims.</p>}
      {blocked.map((item) => <section key={item.seed_key}>
        <p className="cur-hint">{`Not set up: ${item.name}. ${seedBlocked(item.reason)}`}</p>
        {(item.template_ids || []).map(templateId => {
          const key = `${item.seed_key}:${templateId}`;
          return <div key={templateId}>
            <button type="button" className="cur-linkish" aria-expanded={expanded === key}
              disabled={editorBusy || state.saving || loading} onClick={() => setExpanded(expanded === key ? null : key)}>
              {`Inspect/import source: ${templateId}`}
            </button>
            {expanded === key && <TemplateSourceEditor key={key} templateId={templateId}
              disabled={state.saving || loading} onBusyChange={setEditorBusy} onImported={() => load(true)} />}
          </div>;
        })}
      </section>)}
    </ConfirmShell>
  );
};

/** The domain of a sender ("nb.example.com"), never the address. */
const senderDomain = (sender: string): string => {
  const email = senderEmail(sender);
  return email.includes('@') ? email.slice(email.lastIndexOf('@') + 1) : '';
};

/** "Lender · What happens" (spec §14). An unmapped lender shows its domain; while lenders load, a placeholder. Never an address. */
export const exampleLabel = (entry: UsedBy, rules: LenderRule[], ready = true): string => {
  const who = !ready ? '…' : (lenderFor(entry.sender, rules) ?? (senderDomain(entry.sender) || 'A lender'));
  return entry.action ? `${who} · ${actionCopy(entry.action).label}` : who;
};

export const UsedByModal: React.FC<{
  view: WorkflowView; onClose: () => void; onOpenTeach: (target: TeachTarget | null) => void;
}> = ({ view, onClose, onOpenTeach }) => {
  const { rules, ready } = useLenderRulesReady();
  return (
    <Modal title={`Used by ${view.used_by.length} ${view.used_by.length === 1 ? 'example' : 'examples'}`} onClose={onClose}
      footer={(
        <>
          <button type="button" className="ui-btn ui-btn--secondary ui-btn--md" onClick={() => { onOpenTeach(null); onClose(); }}>
            Attach it in Teach the AI
          </button>
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose}>Close</button>
        </>
      )}>
      <ul className="cur-wf-rows">
        {view.used_by.map((entry) => (
          <li key={entry.entry_id}>
            <div className="cur-wf-top">
              <span className="cur-grow">{exampleLabel(entry, rules, ready)}</span>
              <button type="button" className="cur-linkish"
                onClick={() => { onOpenTeach({ entryId: entry.entry_id, sender: entry.sender }); onClose(); }}>
                Show in Teach the AI
              </button>
            </div>
          </li>
        ))}
      </ul>
    </Modal>
  );
};
